/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { IResponseCCS0011 } from "../../../../shared/services/types/response";
import moment from "moment";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { api } from "../../../../shared/services/api";
import { AxiosError, AxiosRequestConfig } from "axios";
import { maskCPFCNPJ } from "../../../../shared/utils/mask";
import ConfirmGenerateResponseModal from "../../../../shared/components/confirmGenerateResponseModal";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  border: "2px solid #cecece",
  overflow: "auto",
  borderRadius: 5,
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

interface ModalDetailProps {
  ccsValue: IResponseCCS0011 | null;
  closeModal: (shouldReload?: boolean) => void;
  open: boolean;
}

const ModalDetail = ({ open, ccsValue, closeModal }: ModalDetailProps) => {
  const token = Cookies.get("@token");
  const [generateIsLoading, setGenerateIsLoading] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [updatingArchive, setUpdatingArchive] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [dropdownValue, setDropdownValue] = useState<string>("01");
  const [observations, setObservations] = useState<string>("");
  const [protocoloStaEntrega, setProtocoloStaEntrega] = useState<string>("");

  useEffect(() => {
    if (ccsValue?.request_service_status) {
      setDropdownValue(ccsValue?.request_service_status);
    }
    if (ccsValue?.text_observation) {
      setObservations(ccsValue?.text_observation);
    }
    if (ccsValue?.psta_entrega) {
      setProtocoloStaEntrega(ccsValue?.psta_entrega);
    }
  }, [ccsValue]);

  const handleDropdownChange = (event: any) => {
    setDropdownValue(event.target.value);
  };

  const handleUpdateArchive = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: "Bearer " + token,
        "Content-type": "application/json",
        Accept: "application/json",
      },
    };

    const body = {
      text_observation: observations,
      request_service_status: dropdownValue,
      psta_entrega: protocoloStaEntrega
    };

    setUpdatingArchive(true);
    try {
      const responseUpdatedReport = await api.put(
        `/reportFinancial/reportUpdate/${ccsValue?.id}`,
        body,
        config
      );

      if (responseUpdatedReport.status === 200) {
        toast.success("Arquivo atualizado com sucesso");
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.error || "Erro de conexão";
        toast.error(errorMessage);
      } else {
        toast.error("Erro inesperado");
      }
    } finally {
      setUpdatingArchive(false);
      setUpdated(true);
    }
    setUpdated(true);
    setUpdatingArchive(false);
  };

  const handleOpenConfirmModal = () => {
    setConfirmModalOpen(true);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
  };

  const handleConfirmGenerate = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: "Bearer " + token,
      },
      responseType: "blob",
    };

    setGenerateIsLoading(true);
    try {
      const responseGenerateReport = await api.get(
        `/archive/response-report/${ccsValue?.id}`,
        config
      );

      if (responseGenerateReport.status === 200) {
        const blob = new Blob([responseGenerateReport.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `CCS0012${ccsValue?.number_control_ccs}.zip`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Arquivo gerado com sucesso");
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.error || "Erro de conexão";
        toast.error(errorMessage);
      } else {
        toast.error("Erro inesperado");
      }
    } finally {
      setGenerateIsLoading(false);
      setConfirmModalOpen(false);
    }
    handleClose(true);
  };

  const resetStates = () => {
    setUpdated(false);
    setDropdownValue("01");
    setObservations("");
  };

  const handleClose = (shouldReload?: boolean) => {
    resetStates();
    closeModal(shouldReload);
  };

  return (
    <Modal
      open={open}
      onClose={() => handleClose(true)}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box sx={{ ...style }}>
        <Box display="flex" alignItems="center" flexWrap="wrap">
          <Typography
            variant="h6"
            sx={{ fontSize: { xs: "1.25rem", sm: "1.25rem", md: "1.5rem" } }}
          >
            Detalhes do Arquivo CCS0011:
          </Typography>
          <Typography
            ml={1}
            color="primary.main"
            variant="h6"
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" } }}
          >
            {ccsValue?.number_control_ccs}
          </Typography>
        </Box>

        <Typography
          variant="body1"
          sx={{ fontSize: { xs: "0.875rem", sm: "1rem" }, mt: 2 }}
        >
          Data de envio Bacen:{" "}
          {moment(ccsValue?.date_time_bacen).format("DD/MM/yyyy")}
        </Typography>

        <Box
          mt={6}
          component="form"
          sx={{ "& > :not(style)": { m: 1 } }}
          noValidate
          autoComplete="off"
        >
          {ccsValue?.partner_name && (
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Parceiro:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {ccsValue?.partner_name}
              </Typography>
            </Box>
          )}
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Nome do tribunal:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {ccsValue?.court_name}
              </Typography>
            </Box>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Código do tribunal:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {ccsValue?.court_code}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Nome do juíz(a):
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {ccsValue?.judge_name}
              </Typography>
            </Box>

            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Cargo:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {ccsValue?.judge_description}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Data de movimento Bacen:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {moment(ccsValue?.date_time_bacen).format("DD/MM/yyyy")}
              </Typography>
            </Box>

            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Data limite de resposta:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {moment(ccsValue?.response_deadline).format("DD/MM/yyyy HH:mm")}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="baseline" mb={1}>
            <Typography
              variant="body1"
              fontWeight="bold"
              mr={1}
              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              Documento do detalhe financeiro:
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            >
              {maskCPFCNPJ(ccsValue?.cpf_cnpj_person)}
            </Typography>
          </Box>

          <Box display="flex" flexWrap="wrap" gap={5}>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Data início:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {moment(ccsValue?.start_date).format("DD/MM/yyyy")}
              </Typography>
            </Box>
            <Box display="flex" alignItems="baseline" mb={1}>
              <Typography
                variant="body1"
                fontWeight="bold"
                mr={1}
                sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
              >
                Data fim:
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {moment(ccsValue?.end_date).format("DD/MM/yyyy")}
              </Typography>
            </Box>
            {
              dropdownValue == "01" &&
              <Box display="flex" alignItems="baseline" mb={1}>
                <Typography variant="body1"
                  color="textSecondary"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Protocolo de Entrega STA:
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                >
                  <input
                    style={{
                      padding: "3px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "0.8rem",
                    }}
                    placeholder="Digite o Protcolo de Entrega"
                    disabled={updated || ccsValue?.status == "DONE"}
                    onChange={(e) => setProtocoloStaEntrega(e.target.value)}
                    value={protocoloStaEntrega}
                  />
                </Typography>
              </Box>
            }
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="dropdown-label">Situação do atendimento</InputLabel>
            <Select
              labelId="dropdown-label"
              value={dropdownValue}
              onChange={handleDropdownChange}
              disabled={updated || ccsValue?.status == "DONE"}
            >
              <MenuItem value="01">01 - Solicitação atendida</MenuItem>
              <MenuItem value="02">
                02 - Relacionamento migrado para outra instituição financeira
              </MenuItem>
              <MenuItem value="03">03 - Relacionamento inexistente</MenuItem>
              <MenuItem value="04">04 - Liminar</MenuItem>
              <MenuItem value="05">05 - Conta inválida</MenuItem>
              <MenuItem value="06">06 - Conta inexistente</MenuItem>
              <MenuItem value="07">07 - Conta encerrada</MenuItem>
              <MenuItem value="08">08 - Agência inexistente</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" }, mb: 1 }}
            >
              Observações:
            </Typography>
            <textarea
              style={{
                width: "100%",
                minHeight: "80px",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "1rem",
              }}
              placeholder="Digite as observações aqui..."
              disabled={updated || ccsValue?.status == "DONE"}
              onChange={(e) => setObservations(e.target.value)}
              value={observations}
            />
          </Box>

          <Box display="flex" flexWrap="wrap" mt={5} gap={2}>
            {ccsValue?.text_observation_request &&
              ccsValue?.text_observation_request
                .split("#")
                .filter((item) => item.trim() !== "")
                .map(function (item) {
                  return <Chip key={item} label={`#${item}`} />;
                })}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            bottom: 16,
            right: 16,
          }}
        >
          {!updated && ccsValue?.status != "DONE" ? (
            <Button
              variant="contained"
              color="primary"
              disabled={updatingArchive}
              onClick={handleUpdateArchive}
              sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            >
              Atualizar informações
            </Button>
          ) : ccsValue?.status != "DONE" ? (
            <Button
              variant="contained"
              color="primary"
              disabled={generateIsLoading}
              onClick={handleOpenConfirmModal}
              sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            >
              Gerar Resposta
            </Button>
          ) : null}

          <Button
            sx={{ marginLeft: 2, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            variant="contained"
            color="error"
            onClick={() => handleClose(true)}
          >
            Cancelar
          </Button>
          <ConfirmGenerateResponseModal
            open={confirmModalOpen}
            isLoading={generateIsLoading}
            onConfirm={handleConfirmGenerate}
            onCancel={handleCloseConfirmModal}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalDetail;
